import dayjs from 'dayjs';
// Component
import { Button, Modal, ModalBody, ModalHeader } from 'plume-ui';
import { ButtonStyles } from 'plume-ui/dist/components/Button/Button';
import { ModalStyles } from 'plume-ui/dist/components/Modal/Modal';
import React, { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useResetRecoilState } from 'recoil';
import { Routes, STANDARD_DATE_TIME_FORMAT } from '../../../config';
import { MixPanelEvents } from '../../../mixPanelEvents';
import { activePartnerAtom, configurationInfoModalAtom } from '../../../state';
// Hooks
import { AdjustableComponent } from '../../../utils/hooks/useClasses';
import { useRedirectToRoute } from '../../../utils/hooks/useRedirectToRoute';
import { useTrackEvent } from '../../trackingAnalytics/hooks/useTrackEvent';

type InfoModalProps = {};

type InfoModalStyles = {};

const InfoModal: AdjustableComponent<InfoModalProps, InfoModalStyles> = () => {
  const { t } = useTranslation();
  const trackEvent = useTrackEvent();
  const modalProps = useRecoilValue(configurationInfoModalAtom);
  const resetModal = useResetRecoilState(configurationInfoModalAtom);
  const activePartner = useRecoilValue(activePartnerAtom);
  const redirectToRoute = useRedirectToRoute();

  useEffect(() => {
    if (!modalProps.isOpen) return;
    trackEvent({
      eventName: MixPanelEvents.INFO_MODAL_OPEN,
      additionalContent: {
        MODAL_TYPE: modalProps.type,
      },
    });
  }, [modalProps.isOpen]);

  return (
    <Modal
      classes={(current: ModalStyles) => ({
        root: `${current.root} Configuration__modal Configuration__modal--info`,
      })}
      isOpen={modalProps.isOpen}
      onRequestClose={resetModal}
      ariaHideApp={false}
    >
      {modalProps.type === 'outreachList' && (
        <ModalHeader
          title={t('configuration.modals.outreachList.noOfOutreach')}
          subtitle={t('configuration.modals.outreachList.total', {
            number: modalProps.data?.monitorDetails?.length || 0,
          })}
        />
      )}
      {modalProps.type === 'lastError' && (
        <ModalHeader
          title={t('configuration.modals.error.errorCode')}
          subtitle={dayjs
            .utc(modalProps.data?.lastError)
            .local()
            .format(STANDARD_DATE_TIME_FORMAT)}
        />
      )}
      {modalProps.type === 'outreachList' && (
        <ModalBody>
          {modalProps.data?.monitorDetails?.map((monitor) => (
            <Button
              classes={(current: ButtonStyles) => ({
                root: `${current.root} Configuration__modal--info__list-button`,
                container: `${current.container} Configuration__modal--info__list-button__container`,
                loader: `${current.loader}`,
              })}
              styleVariant="quaternary"
              key={monitor.monitorId}
              onClick={() => {
                if (modalProps.monitorType === 'autonomous')
                  redirectToRoute(Routes.AutonomousMonitorDetails, {
                    partnerId: activePartner?.id,
                    monitorId: monitor.monitorId,
                    eventId: 0,
                  });
                else
                  redirectToRoute(Routes.MonitorDetails, {
                    partnerId: activePartner?.id,
                    monitorId: monitor.monitorId,
                    eventId: 0,
                  });
                resetModal();
              }}
            >
              {modalProps.monitorType === 'autonomous'
                ? t(
                    `monitors.criteriaNames.${monitor.monitorName?.replaceAll(
                      /[{}]/gi,
                      '',
                    )}`,
                  )
                : monitor.monitorName}
            </Button>
          ))}
        </ModalBody>
      )}
      {modalProps.type === 'lastError' && (
        <ModalBody>
          <div className="Configuration__modal--info__list-item">
            {modalProps.data?.lastErrorMessage}
          </div>
        </ModalBody>
      )}
    </Modal>
  );
};

export default memo(InfoModal);
